import React, { useCallback, useEffect, useState } from "react";
import { MdMessage, MdSave } from "react-icons/md";
import {
  getWhatsappTemplates,
  updateWhatsappTemplates,
} from "services/whatsappCommunicationService";

const LoadIndicator = ({ label }) => (
  <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 dark:bg-white/10 dark:text-gray-200">
    <span className="mr-2 h-2.5 w-2.5 animate-pulse rounded-full bg-brand-500" />
    {label}
  </div>
);

const WhatsAppCommunication = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState({
    leadWelcome: "",
    leadToClient: "",
    clientToLead: "",
    careofWelcome: "",
  });

  const showToast = (message, type = "error") => {
    if (typeof window !== "undefined" && typeof window.__showToast === "function") {
      window.__showToast(message, type);
    }
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWhatsappTemplates();
      if (data?.data?.templates) {
        setTemplates(data.data.templates);
      }
    } catch (error) {
      const message = error?.message || "Unable to fetch templates.";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTemplates((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await updateWhatsappTemplates(templates);
      if (data?.data?.templates) {
        setTemplates(data.data.templates);
        showToast("Templates saved successfully.", "success");
      }
    } catch (error) {
      const message = error?.message || "Unable to save templates.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-brand-500">
              <MdMessage className="h-5 w-5" />
              <h1 className="text-2xl font-semibold text-navy-700 dark:text-white">WhatsApp Communication</h1>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
              Configure automated WhatsApp messages sent during CRM events.
            </p>
          </div>
          <div className="rounded-2xl bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 dark:bg-white/5 dark:text-gray-200">
            Route: <span className="font-semibold">/whatsapp-communication</span>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="mb-6 flex items-start justify-between gap-4 sm:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
              Message Templates
            </p>
            <h2 className="mt-2 text-xl font-semibold text-navy-700 dark:text-white">Automated Triggers</h2>
          </div>
          <div>
            {loading && <LoadIndicator label="Loading templates..." />}
          </div>
        </div>

        {!loading && (
          <div className="space-y-8">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-white/10 dark:bg-white/5">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Lead Welcome Message</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Sent immediately when a new Lead is created.</p>
                <textarea
                  name="leadWelcome"
                  value={templates.leadWelcome || ""}
                  onChange={handleChange}
                  placeholder="e.g. Hi there! Thanks for reaching out..."
                  rows="4"
                  className="mt-4 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-400"
                />
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-white/10 dark:bg-white/5">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Lead to Client Conversion</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Sent when a Lead is successfully converted into a Client.</p>
                <textarea
                  name="leadToClient"
                  value={templates.leadToClient || ""}
                  onChange={handleChange}
                  placeholder="e.g. Congratulations! You are now an official client..."
                  rows="4"
                  className="mt-4 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-400"
                />
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-white/10 dark:bg-white/5">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Client to Lead Reversion</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Sent when a Client's status is changed back to a Lead.</p>
                <textarea
                  name="clientToLead"
                  value={templates.clientToLead || ""}
                  onChange={handleChange}
                  placeholder="e.g. Your account has been updated back to lead status..."
                  rows="4"
                  className="mt-4 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-400"
                />
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-white/10 dark:bg-white/5">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">CareOf Welcome Message</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Sent immediately when a new CareOf contact is added.</p>
                <textarea
                  name="careofWelcome"
                  value={templates.careofWelcome || ""}
                  onChange={handleChange}
                  placeholder="e.g. Hello! Welcome to our CareOf network..."
                  rows="4"
                  className="mt-4 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-400"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-brand-500/20 transition hover:bg-brand-600 disabled:opacity-70"
              >
                <MdSave className="h-5 w-5" />
                {saving ? "Saving..." : "Save Templates"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppCommunication;
